import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Heart, Sparkles, Crown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

export const ENDORSEMENT_KINDS = ["loved", "suggested", "pick"] as const;
export type EndorsementKind = (typeof ENDORSEMENT_KINDS)[number];

export const ENDORSEMENT_ICONS: Record<EndorsementKind, typeof Heart> = {
  loved: Heart,
  suggested: Sparkles,
  pick: Crown,
};

type Endorsement = {
  id: string;
  listing_id: string;
  ambassador_user_id: string;
  kind: EndorsementKind;
  note: string | null;
  created_at: string;
};

type Endorser = {
  id: string;
  display_name: string | null;
  slug: string | null;
  avatar_url: string | null;
  ambassador_title: string | null;
};

/** All endorsements for a listing plus the ambassador profiles behind them. */
export function useListingEndorsements(listingId: string | undefined) {
  return useQuery({
    queryKey: ["listing-endorsements", listingId],
    enabled: Boolean(listingId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listing_endorsements")
        .select("*")
        .eq("listing_id", listingId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as Endorsement[];
      const ids = [...new Set(rows.map((r) => r.ambassador_user_id))];
      let people: Endorser[] = [];
      if (ids.length) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id,display_name,slug,avatar_url,ambassador_title")
          .in("id", ids);
        people = (profiles ?? []) as Endorser[];
      }
      return rows.map((row) => ({
        ...row,
        endorser: people.find((p) => p.id === row.ambassador_user_id) ?? null,
      }));
    },
  });
}

/** Listing ids that carry at least one ambassador endorsement (for board cards). */
export function useEndorsedListingIds() {
  const { data } = useQuery({
    queryKey: ["endorsed-listing-ids"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listing_endorsements")
        .select("listing_id,kind");
      if (error) throw error;
      const map: Record<string, EndorsementKind> = {};
      for (const row of (data ?? []) as { listing_id: string; kind: EndorsementKind }[]) {
        // A "pick" outranks "suggested", which outranks "loved".
        const rank = { loved: 1, suggested: 2, pick: 3 } as const;
        const current = map[row.listing_id];
        if (!current || rank[row.kind] > rank[current]) map[row.listing_id] = row.kind;
      }
      return map;
    },
  });
  return data ?? {};
}

/** Compact badge shown on board cards and lists. */
export function AmbassadorBadge({ kind }: { kind: EndorsementKind }) {
  const { t } = useT();
  const Icon = ENDORSEMENT_ICONS[kind];
  return (
    <Badge
      className="gap-1 border-transparent bg-primary/15 text-primary hover:bg-primary/20"
      title={t("profiles.endorseBadgeHint")}
    >
      <Icon className="size-3" /> {t(`profiles.endorse.${kind}`)}
    </Badge>
  );
}

/**
 * Ambassador superpowers on a listing: give one of three special endorsements
 * and show who endorsed the listing. Visible to everyone, editable by
 * Official Platform Ambassadors (and admins).
 */
export function ListingEndorsements({ listingId }: { listingId: string }) {
  const { t } = useT();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: endorsements = [] } = useListingEndorsements(listingId);

  const { data: me } = useQuery({
    queryKey: ["my-ambassador-flag", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("is_ambassador")
        .eq("id", user!.id)
        .maybeSingle();
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id);
      return {
        isAmbassador: Boolean(data?.is_ambassador),
        isAdmin: (roles ?? []).some((r) => r.role === "admin"),
      };
    },
  });

  const canEndorse = Boolean(me?.isAmbassador || me?.isAdmin);
  const mine = endorsements.filter((e) => e.ambassador_user_id === user?.id);

  async function toggle(kind: EndorsementKind) {
    if (!user?.id) return;
    const existing = mine.find((e) => e.kind === kind);
    if (existing) {
      const { error } = await supabase.from("listing_endorsements").delete().eq("id", existing.id);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success(t("profiles.endorseRemoved"));
    } else {
      const { error } = await supabase
        .from("listing_endorsements")
        .insert({ listing_id: listingId, ambassador_user_id: user.id, kind });
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success(t("profiles.endorseAdded"));
    }
    await queryClient.invalidateQueries({ queryKey: ["listing-endorsements", listingId] });
    await queryClient.invalidateQueries({ queryKey: ["endorsed-listing-ids"] });
  }

  if (!canEndorse && endorsements.length === 0) return null;

  return (
    <section className="card-surface space-y-4 p-4">
      <div>
        <h2 className="font-display text-lg font-bold">{t("profiles.endorseHeading")}</h2>
        <p className="text-sm text-muted-foreground">{t("profiles.endorseIntro")}</p>
      </div>

      {endorsements.length > 0 && (
        <ul className="space-y-2">
          {endorsements.map((e) => {
            const Icon = ENDORSEMENT_ICONS[e.kind];
            const name = e.endorser?.display_name ?? t("profiles.endorseAmbassador");
            return (
              <li key={e.id} className="flex items-center gap-3 rounded-xl border border-border p-3">
                {e.endorser?.avatar_url ? (
                  <img src={e.endorser.avatar_url} alt="" className="size-9 rounded-full object-cover" />
                ) : (
                  <span className="flex size-9 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-foreground">
                    {name.charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {e.endorser?.slug ? (
                      <Link to="/user/$slug" params={{ slug: e.endorser.slug }} search={{ ref: undefined }} className="hover:underline">
                        {name}
                      </Link>
                    ) : (
                      name
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {e.endorser?.ambassador_title || t("profiles.endorseAmbassador")}
                  </p>
                </div>
                <Badge className="gap-1 border-transparent bg-primary/15 text-primary">
                  <Icon className="size-3" /> {t(`profiles.endorse.${e.kind}`)}
                </Badge>
              </li>
            );
          })}
        </ul>
      )}

      {canEndorse && (
        <div className="flex flex-wrap gap-2 border-t border-border pt-3">
          {ENDORSEMENT_KINDS.map((kind) => {
            const Icon = ENDORSEMENT_ICONS[kind];
            const active = mine.some((e) => e.kind === kind);
            return (
              <Button
                key={kind}
                type="button"
                size="sm"
                variant={active ? "hot" : "outline"}
                onClick={() => void toggle(kind)}
              >
                <Icon className="size-4" /> {t(`profiles.endorse.${kind}`)}
              </Button>
            );
          })}
        </div>
      )}
    </section>
  );
}
